import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const dateFmt = (iso) => iso ? new Date(iso).toLocaleString() : "";

export function MembersAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/members").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => {
    await api.patch(`/admin/members/${id}`, { role });
    toast.success("Member updated");
    load();
  };
  return (
    <div>
      <div className="brand-eyebrow">People</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Members</h1>
      <div className="editorial-card mt-8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-alt"><tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Tier</th><th className="p-3 text-left">Role</th><th className="p-3 text-left">Joined</th></tr></thead>
          <tbody>
            {items.map(u => (
              <tr key={u.id} className="border-t border-gold-light" data-testid={`member-row-${u.id}`}>
                <td className="p-3">{u.name}</td>
                <td className="p-3 text-charcoal/70">{u.email}</td>
                <td className="p-3">{u.membership_tier || "community"}</td>
                <td className="p-3">
                  <select defaultValue={u.role} onChange={(e) => setRole(u.id, e.target.value)}
                    className="border border-gold-light rounded px-2 py-1 bg-white" data-testid={`member-role-${u.id}`}>
                    {["free_member", "paid_member", "content_manager", "admin"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="p-3 text-charcoal/60">{dateFmt(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RequestsAdmin() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const load = () => api.get("/admin/speaking-requests").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/admin/speaking-requests/${id}`, { status });
    load();
  };
  const saveNotes = async () => {
    if (!selected) return;
    await api.patch(`/admin/speaking-requests/${selected.id}`, { notes });
    toast.success("Notes saved"); load();
  };
  const statuses = ["new", "reviewing", "follow_up", "tentative", "proposal_sent", "booked", "declined", "completed"];
  return (
    <div>
      <div className="brand-eyebrow">Speaking Requests</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Booking Inquiries</h1>
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="editorial-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream-alt"><tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Org</th><th className="p-3 text-left">Status</th></tr></thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id} onClick={() => { setSelected(r); setNotes(r.notes || ""); }} className={`border-t border-gold-light cursor-pointer ${selected?.id === r.id ? "bg-cream-alt" : ""}`} data-testid={`req-row-${r.id}`}>
                  <td className="p-3">{r.name}</td>
                  <td className="p-3 text-charcoal/70">{r.organization}</td>
                  <td className="p-3">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="editorial-card p-6">
          {selected ? (
            <>
              <div className="brand-eyebrow">{selected.status}</div>
              <h3 className="font-serif text-2xl mt-2">{selected.name} — {selected.organization}</h3>
              <div className="text-sm text-charcoal/60 mt-1">{selected.email} · {selected.phone}</div>
              <dl className="mt-4 text-sm grid grid-cols-2 gap-3">
                <div><dt className="text-charcoal/60">Event type</dt><dd>{selected.event_type}</dd></div>
                <div><dt className="text-charcoal/60">Date</dt><dd>{selected.event_date}</dd></div>
                <div><dt className="text-charcoal/60">Location</dt><dd>{selected.event_location}</dd></div>
                <div><dt className="text-charcoal/60">Audience</dt><dd>{selected.audience_size}</dd></div>
                <div><dt className="text-charcoal/60">Budget</dt><dd>{selected.budget_range}</dd></div>
                <div><dt className="text-charcoal/60">Topic</dt><dd>{selected.topic}</dd></div>
              </dl>
              <p className="mt-4 whitespace-pre-line text-charcoal/85">{selected.message}</p>
              <div className="mt-4">
                <label className="text-xs text-charcoal/60">Status</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {statuses.map(s => (
                    <button key={s} onClick={() => setStatus(selected.id, s)}
                      className={`px-3 py-1 rounded-full text-xs border ${selected.status === s ? "bg-plum text-cream border-plum" : "border-gold-light text-charcoal/70"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs text-charcoal/60">Internal notes</label>
                <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gold-light bg-white px-3 py-2" />
                <button onClick={saveNotes} className="btn-primary mt-3" data-testid="req-save-notes">Save notes</button>
              </div>
            </>
          ) : <div className="text-charcoal/50">Select a request to review.</div>}
        </div>
      </div>
    </div>
  );
}

export function QuestionsAdmin() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [answer, setAnswer] = useState("");
  const load = () => api.get("/admin/questions").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!selected) return;
    await api.patch(`/admin/questions/${selected.id}`, { status: "answered", answer });
    toast.success("Answer saved"); load(); setSelected(null); setAnswer("");
  };
  return (
    <div>
      <div className="brand-eyebrow">Questions</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Q&A Submissions</h1>
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="editorial-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream-alt"><tr><th className="p-3 text-left">User</th><th className="p-3 text-left">Subject</th><th className="p-3 text-left">Status</th></tr></thead>
            <tbody>
              {items.map(q => (
                <tr key={q.id} onClick={() => { setSelected(q); setAnswer(q.answer || ""); }} className={`border-t border-gold-light cursor-pointer ${selected?.id === q.id ? "bg-cream-alt" : ""}`} data-testid={`q-row-${q.id}`}>
                  <td className="p-3">{q.user_name}</td>
                  <td className="p-3">{q.subject} {q.is_priority && <span className="text-gold">★</span>}</td>
                  <td className="p-3">{q.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="editorial-card p-6">
          {selected ? (
            <>
              <h3 className="font-serif text-2xl">{selected.subject}</h3>
              <div className="text-sm text-charcoal/60 mt-1">{selected.user_name} · {selected.user_email}</div>
              <p className="mt-4 whitespace-pre-line text-charcoal/85">{selected.body}</p>
              <label className="mt-6 text-xs text-charcoal/60 block">Auntie's answer</label>
              <textarea rows={6} value={answer} onChange={(e) => setAnswer(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold-light bg-white px-3 py-2" />
              <button onClick={save} className="btn-primary mt-3" data-testid="q-save-answer">Save answer</button>
            </>
          ) : <div className="text-charcoal/50">Select a question to answer.</div>}
        </div>
      </div>
    </div>
  );
}

export function PaymentsAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments").then(r => setItems(r.data)); }, []);
  return (
    <div>
      <div className="brand-eyebrow">Payments</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Transactions</h1>
      <div className="editorial-card mt-8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-alt"><tr><th className="p-3 text-left">When</th><th className="p-3 text-left">Kind</th><th className="p-3 text-left">Ref</th><th className="p-3 text-left">Email</th><th className="p-3 text-right">Amount</th><th className="p-3 text-left">Status</th></tr></thead>
          <tbody>
            {items.map(t => (
              <tr key={t.id} className="border-t border-gold-light" data-testid={`pay-${t.id}`}>
                <td className="p-3 text-charcoal/60">{dateFmt(t.created_at)}</td>
                <td className="p-3">{t.kind}</td>
                <td className="p-3 font-mono text-xs">{t.ref_id}</td>
                <td className="p-3">{t.email}</td>
                <td className="p-3 text-right">${(t.amount || 0).toFixed(2)}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${t.payment_status === "paid" ? "bg-plum text-cream" : "bg-cream-alt text-charcoal/70"}`}>{t.payment_status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-charcoal/50">No transactions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SubscribersAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/subscribers").then(r => setItems(r.data)); }, []);
  return (
    <div>
      <div className="brand-eyebrow">Email List</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Subscribers</h1>
      <div className="editorial-card mt-8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-alt"><tr><th className="p-3 text-left">Email</th><th className="p-3 text-left">Name</th><th className="p-3 text-left">Subscribed</th></tr></thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id} className="border-t border-gold-light" data-testid={`sub-${s.id}`}>
                <td className="p-3">{s.email}</td>
                <td className="p-3">{s.name}</td>
                <td className="p-3 text-charcoal/60">{dateFmt(s.subscribed_at)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-charcoal/50">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SettingsAdmin() {
  const [s, setS] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(r => setS(r.data || {})); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", s); toast.success("Saved"); }
    finally { setBusy(false); }
  };
  const F = (k, label) => (
    <div>
      <label className="text-sm text-charcoal/70">{label}</label>
      <input value={s[k] ?? ""} onChange={(e) => setS({...s, [k]: e.target.value})}
        className="mt-2 w-full rounded-md border border-gold-light bg-white px-3 py-2" data-testid={`setting-${k}`} />
    </div>
  );
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const webhookUrl = `${backend}/api/webhook/session-ended`;
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };
  return (
    <div>
      <div className="brand-eyebrow">Site</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Settings</h1>
      <div className="editorial-card mt-8 p-6 max-w-2xl space-y-4">
        {F("site_name", "Site name")}
        {F("tagline", "Tagline")}
        {F("contact_email", "Contact email")}
        {F("instagram", "Instagram URL")}
        {F("facebook", "Facebook URL")}
        {F("linkedin", "LinkedIn URL")}
        <button onClick={save} disabled={busy} className="btn-primary" data-testid="settings-save">{busy ? "…" : "Save"}</button>
      </div>

      <div className="editorial-card mt-8 p-6 max-w-3xl">
        <div className="brand-eyebrow">Streaming Auto-Replay</div>
        <h2 className="font-serif text-2xl mt-2">Zoom · StreamYard · Zapier webhook</h2>
        <p className="mt-3 text-charcoal/70 text-sm leading-relaxed">
          When a live session ends, the site can automatically copy the livestream URL into the replay
          slot with a 30-day expiry — no manual click needed. Two ways to trigger it:
        </p>

        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-charcoal/50">Option 1 — Automatic (already on)</div>
          <p className="mt-2 text-sm text-charcoal/80">
            The scheduler sweeps every 5 minutes. Any event whose <b>Ends At</b> is in the past
            and has a Livestream URL but no Replay URL is auto-swapped. Set the <b>Ends At</b> field
            when you create the event and you're done.
          </p>
        </div>

        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-charcoal/50">Option 2 — Webhook (Zoom / StreamYard / Zapier)</div>
          <p className="mt-2 text-sm text-charcoal/80">
            Configure Zoom's <code>webinar.ended</code> event (or a Zapier "When broadcast ends" trigger for StreamYard)
            to POST to this URL with the shared secret header.
          </p>
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-charcoal/60 w-24">URL:</span>
              <code className="flex-1 bg-cream-alt px-3 py-2 rounded font-mono text-xs break-all" data-testid="webhook-url">{webhookUrl}</code>
              <button onClick={() => copy(webhookUrl)} className="btn-outline !py-1 !px-3 text-xs" data-testid="copy-webhook-url">Copy</button>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-charcoal/60 w-24">Header:</span>
              <code className="flex-1 bg-cream-alt px-3 py-2 rounded font-mono text-xs">X-Webhook-Secret: &lt;WEBHOOK_SECRET&gt;</code>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-charcoal/60 w-24">Body:</span>
              <code className="flex-1 bg-cream-alt px-3 py-2 rounded font-mono text-xs">{'{"external_id":"<Zoom webinar id>","expires_in_days":30}'}</code>
            </div>
          </div>
          <p className="mt-3 text-xs text-charcoal/50">
            Set each event's <b>External ID</b> field (in Events admin) to its Zoom webinar ID or StreamYard broadcast ID.
            The webhook matches by <code>external_id</code> (or you can send <code>event_id</code> directly).
            Store the shared <code>WEBHOOK_SECRET</code> in your Zoom app / Zapier configuration — keep it private.
          </p>
        </div>
      </div>
    </div>
  );
}
